import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
// Importing this installs the axios interceptors that carry the session cookie, the
// portal name and the CSRF token on every request. See ../session.js.
import { clearSession, revokeSession } from '../session';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AuthContext = createContext();

// HIPAA: automatic logoff after inactivity (4 hours)
const IDLE_TIMEOUT_MS = 240 * 60 * 1000;

export function useAuth() {
  return useContext(AuthContext);
}

// The server refuses every other call with this 403 while an administrator-set
// password is still in place.
const MUST_CHANGE_PASSWORD_RE = /must change the password/i;

// GET /auth/me is how a reload finds out whether there is a session at all, so its own
// 401 is an answer rather than an error — it must not bounce the browser to /login.
const AUTH_ME_RE = /\/auth\/me$/;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  // Kept apart from `user` because `user` is replaced wholesale whenever the server
  // sends a fresh profile, and not every one of those carries this flag.
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const logoutRef = useRef(null);
  const idleTimerRef = useRef(null);

  const logout = useCallback(() => {
    const revoked = revokeSession();
    clearSession();
    setUser(null);
    setIsAuthenticated(false);
    setMustChangePassword(false);
    return revoked;
  }, []);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  /** Adopt the profile the server just sent as the signed-in admin. */
  const applySession = useCallback((userData) => {
    // This portal is the super admin's alone; anything else that reaches it is not a
    // session it can show.
    if (!userData || userData.role !== 'super_admin') {
      return false;
    }
    setUser(userData);
    setMustChangePassword(!!userData.must_change_password);
    setIsAuthenticated(true);
    return true;
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        // Mask unexpected/server errors (5xx, network) with a friendly message;
        // keep actionable 4xx messages (validation, auth, conflicts) intact.
        const st = err?.response?.status;
        if (!err?.response || st >= 500) {
          const FRIENDLY = 'Something went wrong. Our team is looking into it.';
          if (!err.response) err.response = { status: 0, data: {} };
          if (!err.response.data || typeof err.response.data !== 'object') err.response.data = {};
          const eid = err.response.data.error_id;
          err.response.data.detail = eid ? `${FRIENDLY} (ref: ${eid})` : FRIENDLY;
          err.userMessage = err.response.data.detail;
        }
        const url = String(err?.config?.url || '');
        const detail = err?.response?.data?.detail;
        if (st === 403 && typeof detail === 'string' && MUST_CHANGE_PASSWORD_RE.test(detail)) {
          setMustChangePassword(true);
          return Promise.reject(err);
        }
        const isAuthLogin =
          url.includes('/auth/login') || url.includes('/auth/token') || url.includes('/auth/register');
        const isMe = AUTH_ME_RE.test(url.split('?')[0]);
        if (err?.response?.status === 401 && !isAuthLogin && !isMe) {
          logoutRef.current?.();
          window.location.href = '/login';
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Boot and every reload start here. The session is in a cookie this page cannot read,
  // so the server is the only thing that knows whether there is one: 200 means signed
  // in and carries the profile and the CSRF token; 401 means signed out.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/auth/me`)
      .then((res) => {
        if (cancelled) return;
        if (!applySession(res.data)) {
          // Somebody else's session in this portal's cookie — end it rather than show
          // a half-signed-in shell.
          logoutRef.current?.();
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUser(null);
          setIsAuthenticated(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [applySession]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        logoutRef.current?.();
      }, IDLE_TIMEOUT_MS);
    };
    resetIdleTimer();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer));
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
    };
  }, [isAuthenticated]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/v1/auth/login/clinic`, { email, password });
    const { user: userData } = response.data;
    if (userData.role !== 'super_admin') {
      throw new Error('Super admin access required');
    }
    applySession(userData);
    return userData;
  };

  /**
   * After POST /auth/me/password succeeds. The change signs out every earlier token
   * (including the one that made the request) and issues a new one in the cookie, so
   * adopt the profile and CSRF token that came back with it.
   */
  const completePasswordChange = useCallback((data) => {
    if (data?.user) {
      applySession(data.user);
    }
    setMustChangePassword(!!data?.user?.must_change_password);
  }, [applySession]);

  const value = {
    user, isAuthenticated, loading, login, logout, applySession,
    mustChangePassword, completePasswordChange,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
